/**
 * Footer component for Stack Forward website
 */

import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Footer link interface
 */
interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

/**
 * Footer component
 */
export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  // Footer links
  const footerLinks: FooterLink[] = [
    { label: 'Privacy Policy', href: '/privacy', external: false },
    {
      label: 'Email Us',
      href: 'mailto:hello@stackforward.org',
      external: true,
    },
  ];

  // Handle external link clicks
  const handleExternalLinkClick = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <footer className="bg-neutral-900 text-white">
      <div className="container-custom">
        <div className="py-12">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Brand section */}
            <div className="space-y-4">
              <h3 className="text-2xl font-bold">
                <span className="text-white">Stack </span>
                <span className="text-orange-300">Forward</span>
              </h3>
              <p className="text-neutral-300 max-w-md">
                Independent open-source consulting serving businesses of all
                sizes. We help startups, SMEs, and enterprises implement, host,
                and support open-source systems.
              </p>
              <div className="flex items-center space-x-2 text-sm text-neutral-400">
                <span>🌍</span>
                <span>Middle East & India Delivery</span>
              </div>
            </div>

            {/* Quick links */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Quick Links</h4>
              <nav className="space-y-2">
                {footerLinks.map(link =>
                  link.external ? (
                    <button
                      key={link.href}
                      onClick={() => handleExternalLinkClick(link.href)}
                      className="block text-neutral-300 hover:text-white transition-colors duration-200 text-left"
                    >
                      {link.label}
                      <span className="ml-1 text-xs" aria-label="External link">
                        ↗
                      </span>
                    </button>
                  ) : (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="block text-neutral-300 hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </nav>
            </div>

            {/* Contact info */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Get in Touch</h4>
              <div className="space-y-2 text-neutral-300">
                <p>
                  <span className="font-medium">Email:</span>{' '}
                  <a
                    href="mailto:hello@stackforward.org"
                    className="hover:text-white transition-colors duration-200"
                  >
                    hello@stackforward.org
                  </a>
                </p>
                <p>
                  <span className="font-medium">Website:</span>{' '}
                  <Link
                    to="/"
                    className="hover:text-white transition-colors duration-200"
                  >
                    stackforward.org
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Bottom section */}
          <div className="mt-8 pt-8 border-t border-neutral-800">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              {/* Copyright */}
              <div className="text-neutral-400 text-sm">
                © {currentYear} Stack Forward. All rights reserved.
              </div>

              {/* Additional info */}
              <div className="text-neutral-400 text-sm text-center md:text-right">
                Independent Open-Source Consulting
              </div>
            </div>

            {/* Legal disclaimer */}
            <div className="mt-4 pt-4 border-t border-neutral-800">
              <p className="text-xs text-neutral-500 text-center">
                Stack Forward is an independent consulting firm specializing in
                open-source enterprise solutions. We are not affiliated with
                ERPNext, Odoo, Mattermost, Nextcloud, Keycloak, GitLab, or
                MinIO.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
